import { useState, useEffect } from 'react';
import { Box, Button, ButtonGroup, Typography, Grid } from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';
type Item = {
    label: string;
    value: any;
};

// Transparent variant of the primary color that follows the active color
// scheme: uses the CSS variable channel when available (light/dark aware).
function primaryAlpha(theme: Theme, opacity: number): string {
    return theme.vars
        ? `rgba(${theme.vars.palette.primary.mainChannel} / ${opacity})`
        : alpha(theme.palette.primary.main, opacity);
}

type Props = {
    grid?: boolean;
    name?: string;
    caption?: string;
    items?: Item[];
    selected?: any | any[];
    onChange?: (params: { name?: string; value: any }) => void;
    exclusive?: boolean;
    size?: 'small' | 'medium' | 'large';
    variant?: 'text' | 'outlined' | 'contained';
    disabled?: boolean;
    [key: string]: any;
};

// Helper to normalize selected prop to array
function normalizeSelected(selected: any | any[] | undefined): any[] {
    if (Array.isArray(selected)) return selected;
    if (selected !== undefined && selected !== null) return [selected];
    return [];
}

/**
 * SttSelectableItems: toggle one or many of a list of simple items. They are rendered as a button group (for the free styling and feedback)
 * bool grid - wrap the component inside a <Grid>
 * string name - field name
 * string caption - caption for the label
 * Object[] items - array of {label, value} pairs representing the available items
 * array selected - array of selected VALUES, for ex. [0,1,4,5], or ['yes', 'no', 'perhaps']. The values must correlate with the ones in items
 * Function onChange - callback for when an item is selected / deselected. It will be called with the array of selected items AFTER the change.
 * bool exclusive - if true only one item can be selected at a time
 */
export function SttSelectableItems({
    grid = false,
    name,
    caption,
    items,
    selected,
    onChange,
    exclusive = false,
    size = 'medium',
    variant = 'contained',
    disabled,
    ...rest
}: Props): React.ReactElement {
    // Use internal state to handle rapid clicks correctly
    const [internalSelected, setInternalSelected] = useState<any[]>(() =>
        normalizeSelected(selected),
    );

    // Sync internal state with external prop when it changes from outside
    useEffect(() => {
        setInternalSelected(normalizeSelected(selected));
    }, [selected]);

    function select(value: any, isSelected: boolean) {
        if (isSelected) {
            // In exclusive mode, we can only select one item
            if (exclusive) {
                setInternalSelected([value]);
                onChange?.({ name, value });
            } else {
                // Use functional update to always get the latest state
                setInternalSelected((prev) => {
                    const newSelected = [...prev, value];
                    onChange?.({ name, value: newSelected });
                    return newSelected;
                });
            }
        } else {
            // Use functional update to always get the latest state
            setInternalSelected((prev) => {
                const newSelected = prev.filter((v) => v !== value);
                onChange?.({ name, value: newSelected });
                return newSelected;
            });
        }
    }

    const content = (
        <Box position="relative">
            {caption && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: -12,
                        left: 12,
                        backgroundColor: 'background.paper',
                        px: 1,
                        zIndex: 10,
                        borderRadius: 0.5,
                    }}
                >
                    <Typography
                        sx={{
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                        }}
                    >
                        {caption}
                    </Typography>
                </Box>
            )}
            <ButtonGroup
                fullWidth
                size={size}
                variant={variant}
                sx={{
                    '& .MuiButtonGroup-grouped': {
                        minWidth: 40,
                        transition: 'all 0.2s ease-in-out',
                    },
                }}
            >
                {items?.map((item: Item, i: number) => {
                    const isSelected = internalSelected.includes(item.value);
                    return (
                        <Button
                            disabled={disabled}
                            key={item.value ?? i}
                            color={isSelected ? 'primary' : 'inherit'}
                            onClick={() => select(item.value, !isSelected)}
                            sx={(theme) => ({
                                fontWeight: isSelected ? 700 : 500,
                                letterSpacing: 0.5,
                                ...(disabled
                                    ? isSelected
                                        ? {
                                              backgroundColor: primaryAlpha(
                                                  theme,
                                                  0.6,
                                              ),
                                              color: (theme.vars || theme)
                                                  .palette.primary.contrastText,
                                          }
                                        : {
                                              backgroundColor: (
                                                  theme.vars || theme
                                              ).palette.action
                                                  .disabledBackground,
                                          }
                                    : isSelected
                                      ? {
                                            boxShadow: `0 2px 6px ${primaryAlpha(theme, 0.4)}`,
                                            '&:hover': {
                                                boxShadow: `0 4px 10px ${primaryAlpha(theme, 0.5)}`,
                                            },
                                        }
                                      : {
                                            backgroundColor: (
                                                theme.vars || theme
                                            ).palette.action.hover,
                                            '&:hover': {
                                                backgroundColor: (
                                                    theme.vars || theme
                                                ).palette.action.selected,
                                            },
                                        }),
                            })}
                        >
                            {item.label}
                        </Button>
                    );
                })}
            </ButtonGroup>
        </Box>
    );

    return grid ? <Grid {...rest}>{content}</Grid> : content;
}
